import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps, ReactNode } from 'react';
import styled from 'styled-components';

import { Horizontal, SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelL } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Fan } from '@sorare/core/src/components/animations/Fan';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { range } from '@sorare/core/src/lib/arrays';
import { isType } from '@sorare/core/src/lib/gql';
import { mergeRewardsConfig } from '@sorare/core/src/lib/rewards';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { DumbTask } from '../DumbTask';
import type { DailyTask_taskInterface } from './__generated__/index.graphql';

const Img = styled.img`
  width: var(--triple-unit);
  @media ${laptopAndAbove} {
    width: var(--quadruple-unit);
  }
`;
const Footer = styled(Horizontal).attrs({ gap: 2 })`
  justify-content: space-between;
  @media ${laptopAndAbove} {
    justify-content: flex-end;
  }
`;
const Wrapper = styled.div`
  width: calc(var(--unit) * 6);
`;
const Action = styled.div`
  @media ${laptopAndAbove} {
    border-right: 1px solid var(--c-nd-150);
    padding-right: var(--double-unit);
  }
`;

type Props = {
  dailyTask: DailyTask_taskInterface;
  featured?: boolean;
  borderColor?: ComponentProps<typeof DumbTask>['borderColor'];
  action: ReactNode;
};
export const DailyTask = ({
  dailyTask,
  featured,
  borderColor,
  action,
}: Props) => {
  const { templateIconUrl, iconUrl, title, description, rewardConfigs } =
    dailyTask;

  const mergedRewards = mergeRewardsConfig(rewardConfigs);
  const reward = (
    <Horizontal gap={0.5}>
      {isType(dailyTask, 'DecisivePlayerPickerTask') &&
      !mergedRewards.length ? (
        <Wrapper>
          <Fan>
            {range(dailyTask.maxAppearancesCount).map(i => (
              <img
                key={i}
                width="100%"
                src={dailyTask.possibleRewardConfigsIconUrl || ''}
                alt=""
              />
            ))}
          </Fan>
        </Wrapper>
      ) : (
        mergedRewards.map(rewardConfig => (
          <AnyRewardConfig
            key={rewardConfig.id}
            anyRewardConfig={rewardConfig}
            variant={Variant.CHIP}
          />
        ))
      )}
    </Horizontal>
  );

  return (
    <DumbTask
      borderColor={borderColor}
      featured={featured}
      icon={!!templateIconUrl && <Img src={templateIconUrl} alt="" />}
      title={
        <Horizontal>
          <LabelL bold>{title}</LabelL>
          {!!iconUrl && <img width={20} src={iconUrl} alt="" />}
        </Horizontal>
      }
      description={
        <BodyS color="var(--c-nd-600)">
          <Ellipsis visibleLines={2}>{description}</Ellipsis>
        </BodyS>
      }
      footer={
        featured ? (
          <SBHorizontal>
            {action}
            {reward}
          </SBHorizontal>
        ) : (
          <Footer>
            <Action>{action}</Action>
            {reward}
          </Footer>
        )
      }
    />
  );
};

DailyTask.fragments = {
  taskInterface: gql`
    fragment DailyTask_taskInterface on TaskInterface {
      id
      iconUrl
      templateIconUrl
      title
      description
      possibleRewardConfigsIconUrl
      rewardConfigs {
        id
        ...AnyRewardConfig_anyRewardConfigInterface
        ...mergeRewardsConfig_anyRewardConfigInterface
      }
      ... on DecisivePlayerPickerTask {
        id
        maxAppearancesCount
      }
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${mergeRewardsConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<DailyTask_taskInterface>,
};
